import React, { useEffect, useRef, useState } from "react";
import { FileText, Briefcase, Circle, Sparkles, Users, Check } from "lucide-react";

/**
 * HeroDemo - a ~51s seamless, looping product demo for the public landing
 * hero, following the canonical Intuiterview journey: 01 Application Ready →
 * 02 Predict & Prepare → 03 Practice → 04 Official AI Interview
 * (Official Readiness Score) → 05 Interview Briefs. Pure CSS/React motion -
 * no video download, fast and responsive. All scores illustrative.
 */

const SCENES = [
  { key: "application", label: "01 · Stand Out", tag: "Your application gets stronger.", dur: 12000 },
  { key: "predict", label: "02 · Predict & Prepare", tag: "Know what they'll ask - and what you'll say.", dur: 8000 },
  { key: "practice", label: "03 · Practice", tag: "Rehearse before it counts.", dur: 10000 },
  { key: "official", label: "04 · Official AI Interview", tag: "Prove it officially.", dur: 13000 },
  { key: "briefs", label: "05 · Interview Briefs", tag: "Walk in prepared.", dur: 8000 },
];

const CSS = `
@keyframes hd-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
@keyframes hd-eq { 0%,100% { height: 3px; } 50% { height: 12px; } }
@keyframes hd-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
@keyframes hd-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
@keyframes hd-m-half { 0%,100% { opacity: 0.12; } 22% { opacity: 0.9; } 48% { opacity: 0.28; } 74% { opacity: 0.82; } }
@keyframes hd-m-open { 0%,100% { opacity: 0; } 30% { opacity: 0.85; } 55% { opacity: 0.08; } 82% { opacity: 0.55; } }
@keyframes hd-m-ee { 0%,38%,72%,100% { opacity: 0; } 52% { opacity: 0.75; } }
@keyframes hd-m-o { 0%,26%,100% { opacity: 0; } 12% { opacity: 0.8; } 64% { opacity: 0; } 78% { opacity: 0.5; } }
@keyframes hd-sway { 0%,100% { transform: scale(1.03) translate3d(0,0,0); } 50% { transform: scale(1.045) translate3d(-1.5px,1.5px,0); } }
.hd-rise { animation: hd-rise 0.55s cubic-bezier(0.22,1,0.36,1) both; }
.hd-eq span { width: 2.5px; border-radius: 2px; background: currentColor; animation: hd-eq 0.85s ease-in-out infinite; }
.hd-eq span:nth-child(2) { animation-delay: 0.16s; } .hd-eq span:nth-child(3) { animation-delay: 0.32s; }
.hd-shimmer { background: linear-gradient(90deg, rgba(245,158,11,0) 0%, rgba(245,158,11,0.22) 50%, rgba(245,158,11,0) 100%); background-size: 200% 100%; animation: hd-shimmer 1.1s linear infinite; }
@media (prefers-reduced-motion: reduce) { .hd-anim, .hd-anim * { animation: none !important; } }
`;

function useCountUp(active, from, to, ms) {
  const [v, setV] = useState(from);
  useEffect(() => {
    if (!active) { setV(from); return; }
    let raf; const t0 = performance.now();
    const step = (t) => {
      const p = Math.min((t - t0) / ms, 1);
      setV(Math.round(from + (to - from) * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, from, to, ms]);
  return v;
}

/* ---------- Act 1: APPLICATION READY (Analyze Fit → Improve → Cover Letter) ---------- */
const ACTIONS = ["Analyze Fit", "Improve My Resume", "Create My Cover Letter"];
const ActionChip = ({ n, label, state }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full border px-1.5 sm:px-2 py-0.5 text-[8px] sm:text-[9px] font-semibold transition-colors duration-500 ${
      state === "done"
        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
        : state === "active"
          ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
          : "border-zinc-800 bg-zinc-900/60 text-zinc-500"
    }`}
  >
    {state === "done" ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : <span className="tabular-nums">{n}</span>}
    <span className="truncate">{label}</span>
  </span>
);

const SceneApplication = ({ phase }) => {
  const pct = useCountUp(true, 0, 72, 1600);
  const pct2 = useCountUp(phase >= 2, 72, 91, 1100);
  const value = phase >= 2 ? pct2 : pct;
  const states = [
    phase >= 1 ? "done" : "active",
    phase >= 2 ? "done" : phase === 1 ? "active" : "todo",
    phase >= 3 ? "done" : phase === 2 ? "active" : "todo",
  ];
  return (
    <div className="p-4 sm:p-5 h-full flex flex-col justify-center gap-2.5">
      <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap">
        {ACTIONS.map((a, i) => <ActionChip key={a} n={i + 1} label={a} state={states[i]} />)}
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <div className="hd-rise rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500"><FileText className="h-3 w-3 text-amber-400" /> Your resume</div>
          <div className="mt-2 space-y-1.5">{[86, 70, 92, 58].map((w, i) => <div key={i} className="h-1.5 rounded bg-zinc-800" style={{ width: `${w}%` }} />)}</div>
        </div>
        <div className="hd-rise rounded-lg border border-zinc-800 bg-zinc-900/60 p-3" style={{ animationDelay: "0.12s" }}>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500"><Briefcase className="h-3 w-3 text-amber-400" /> Job description</div>
          <div className="mt-2 space-y-1.5">{[92, 64, 80, 72].map((w, i) => <div key={i} className="h-1.5 rounded bg-zinc-800" style={{ width: `${w}%` }} />)}</div>
        </div>
      </div>
      <div className="hd-rise rounded-lg border border-zinc-800 bg-zinc-900/60 p-3.5" style={{ animationDelay: "0.22s" }}>
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">{phase >= 2 ? "After improvement" : "Fit score"}</span>
          <span className={`text-lg font-semibold tabular-nums ${phase >= 2 ? "text-amber-300" : "text-zinc-100"}`}>{value}%</span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-zinc-800 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-amber-600 to-amber-400 transition-all duration-1000 ease-out" style={{ width: `${value}%` }} />
        </div>
        <div className="mt-2.5 h-5">
          {phase === 0 && (
            <div className="hd-shimmer inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 px-2.5 py-0.5 text-[10px] text-amber-300" style={{ animationDelay: "0.4s" }}>
              <Sparkles className="h-3 w-3" /> Analyzing how well your resume fits this job…
            </div>
          )}
          {phase === 1 && (
            <div className="hd-shimmer inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 px-2.5 py-0.5 text-[10px] text-amber-300">
              <Sparkles className="h-3 w-3" /> AI improving your resume for this role…
            </div>
          )}
          {phase === 2 && (
            <div className="hd-rise inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-semibold text-amber-300">
              +19 · Resume improved
            </div>
          )}
          {phase >= 3 && (
            <div className="hd-rise inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-300">
              <Check className="h-3 w-3" strokeWidth={3} /> Cover letter created. Application ready.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- Act 2: PREDICT & PREPARE ---------- */
const QUESTIONS = [
  { tag: "From your resume", q: "Your resume says you grew activation 32% at Loopwork — walk me through what actually drove that number." },
  { tag: "Resume × job description", q: "This role owns a B2B roadmap, but most of your experience is B2C — how would you adapt your discovery process?" },
  { tag: "Career signal", q: "You moved from engineering into product in 2023 — what convinced you, and how does that background shape your PM style?" },
];
const ScenePredict = () => (
  <div className="p-4 sm:p-5 h-full flex flex-col justify-center gap-2 sm:gap-2.5">
    <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-0.5">Predicted for your interview</div>
    {QUESTIONS.map(({ tag, q }, i) => (
      <div key={i} className="hd-rise rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 sm:px-3.5 py-2 sm:py-2.5 flex items-start gap-2.5" style={{ animationDelay: `${0.15 + i * 0.28}s` }}>
        <span className="mt-0.5 h-4 w-4 rounded bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[9px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
        <span className="min-w-0">
          <span className="block text-[8px] uppercase tracking-[0.18em] text-amber-400/80 mb-0.5">{tag}</span>
          <span className="block text-[11px] sm:text-[12px] text-zinc-200 leading-snug">{q}</span>
        </span>
      </div>
    ))}
    <div className="hd-rise max-[359px]:hidden text-[10px] text-zinc-500" style={{ animationDelay: "1.1s" }}>Built from your resume + this job description</div>
  </div>
);

/* ---------- Act 3: PREPARE & PRACTICE (answers + Mock Interview) ---------- */
const ScenePractice = () => (
  <div className="p-4 sm:p-5 h-full flex flex-col justify-center gap-2.5">
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] uppercase tracking-widest text-amber-400">Practice</span>
      <span className="text-[8px] uppercase tracking-widest font-bold text-sky-300 border border-sky-500/30 rounded px-1.5 py-0.5">Practice mode</span>
    </div>
    <div className="hd-rise rounded-lg border border-zinc-800 bg-zinc-900/60 px-3.5 py-2.5" style={{ animationDelay: "0.1s" }}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Your STAR answer · AI feedback</div>
      <div className="text-[11px] text-zinc-300 leading-snug">Strong structure, quantify the result to land the answer. <span className="text-amber-300">Draft updated.</span></div>
    </div>
    <div className="hd-rise relative rounded-lg border border-sky-500/25 bg-zinc-900 overflow-hidden h-24 sm:h-28" style={{ animationDelay: "0.3s" }}>
      <div className="absolute inset-0 bg-[radial-gradient(80%_80%_at_50%_30%,rgba(63,63,70,0.5),rgba(9,9,11,0.9))]" />
      <div className="absolute left-1/2 top-5 -translate-x-1/2 h-9 w-9 rounded-full bg-zinc-700/80" />
      <div className="absolute left-1/2 top-14 -translate-x-1/2 h-7 w-14 rounded-t-full bg-zinc-700/70" />
      <div className="absolute top-2 left-2.5 inline-flex items-center gap-1.5 rounded-full bg-rose-500/90 px-2 py-0.5 text-[9px] font-semibold text-white">
        <Circle className="h-1.5 w-1.5 fill-white" style={{ animation: "hd-pulse 1.2s ease-in-out infinite" }} /> REC 01:24
      </div>
      <div className="absolute top-2 right-2.5 text-[9px] font-semibold uppercase tracking-widest text-sky-300">Mock Interview</div>
      <div className="absolute bottom-2 left-2.5 inline-flex items-end gap-[3px] h-3 text-amber-400 hd-eq"><span /><span /><span /></div>
      <div className="absolute bottom-2 right-2.5 text-[9px] text-zinc-400">Rehearsing…</div>
    </div>
    <div className="hd-rise text-[10px] text-zinc-500" style={{ animationDelay: "0.55s" }}>Practice with AI as many times as you need, before it counts.</div>
  </div>
);

/* ---------- Act 4: OFFICIAL AI INTERVIEW → Official Readiness Score ---------- */
const PERSONAS = ["hr_officer", "hiring_manager", "executive", "recruiter", "tech_lead", "panel"];
const OfficialScore = ({ phase }) => {
  const score = useCountUp(true, 0, 91, 1600);
  const ready = phase >= 2;
  return (
    <div className="hd-rise absolute inset-0 z-10 bg-zinc-950/90 backdrop-blur-sm flex flex-col items-center justify-center gap-2.5">
      <div className="text-[10px] uppercase tracking-widest text-amber-400">Official Readiness Score</div>
      <div className="relative h-24 w-24">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
          <circle cx="50" cy="50" r="42" fill="none" stroke="rgb(39,39,42)" strokeWidth="8" />
          <circle cx="50" cy="50" r="42" fill="none" stroke={ready ? "rgb(52,211,153)" : "rgb(245,158,11)"} strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={`${(score / 100) * 264} 264`} style={{ transition: "stroke 0.6s ease, stroke-dasharray 0.2s linear" }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-2xl font-semibold tabular-nums ${ready ? "text-emerald-300" : "text-amber-300"}`}>{score}</span>
        </div>
      </div>
      <div className="h-6">
        {ready && (
          <div className="hd-rise inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-[10px] font-semibold text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Interview Ready
          </div>
        )}
      </div>
    </div>
  );
};

const SceneOfficial = ({ active, phase }) => (
  <div className="h-full flex flex-col">
    <div className="relative flex-1 overflow-hidden">
      <div className="absolute inset-0" style={{ animation: active ? "hd-sway 8s ease-in-out infinite" : "none" }}>
        <img src="/avatars/hiring_manager_base.webp" alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 20%" }} />
        {[["half", "hd-m-half 0.62s ease-in-out infinite"], ["open", "hd-m-open 0.86s ease-in-out infinite"], ["ee", "hd-m-ee 1.9s ease-in-out infinite"], ["o", "hd-m-o 2.4s ease-in-out infinite"]].map(([s, anim]) => (
          <img key={s} src={`/avatars/hiring_manager_${s}.webp`} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 20%", opacity: 0, animation: active && phase < 1 ? anim : "none" }} />
        ))}
      </div>
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 90% at 50% 30%, transparent 55%, rgba(9,9,11,0.6) 100%)" }} />
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-zinc-950/95 to-transparent pointer-events-none" />
      <div className="absolute top-2.5 left-2.5 inline-flex items-center rounded-full border border-amber-500/40 bg-zinc-950/70 backdrop-blur-sm px-2 py-0.5 text-[8px] font-semibold uppercase tracking-widest text-amber-300">
        Official assessment
      </div>
      <div className="absolute top-2.5 right-2.5 inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/15 backdrop-blur-sm px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-amber-300">
        <span className="hd-eq inline-flex items-end gap-[2px] h-3 text-amber-400"><span /><span /><span /></span> Speaking
      </div>
      <div className="absolute left-3 bottom-2">
        <div className="text-[12px] font-semibold text-zinc-50">Daniel Mercer</div>
        <div className="text-[9px] text-zinc-400 tracking-wide">Line Manager · Official AI Interview</div>
      </div>
      <div className="hd-rise absolute left-3 right-3 top-8 sm:top-9" style={{ animationDelay: "0.5s" }}>
        <div className="max-w-[75%] rounded-lg bg-zinc-950/70 backdrop-blur-sm border border-zinc-800/80 px-2.5 py-1.5 text-[10px] text-zinc-200 leading-snug">
          "Tell me about the result you're most proud of, and what it cost you to get there."
        </div>
      </div>
      {phase >= 1 && <OfficialScore phase={phase} />}
    </div>
    <div className="px-3.5 py-2 border-t border-zinc-800/80 bg-zinc-950/90 flex items-center gap-2 overflow-hidden">
      <span className="hidden sm:inline text-[9px] uppercase tracking-widest text-zinc-500 flex-shrink-0">Choose your interviewer</span>
      <span className="sm:hidden text-[9px] uppercase tracking-widest text-zinc-500 flex-shrink-0">Interviewers</span>
      <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
        {PERSONAS.map((p, i) => (
          <span key={p} className={`relative h-6 w-6 rounded-full overflow-hidden ring-1 ${i === 1 ? "ring-amber-400" : "ring-zinc-700"}`}>
            <img src={`/avatars/${p}_base.webp`} alt="" loading="lazy" className="h-full w-full object-cover" style={{ objectPosition: "center 15%" }} />
          </span>
        ))}
        <span className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-900 px-1.5 py-0.5 text-[8px] uppercase tracking-wider text-zinc-400"><Users className="h-2.5 w-2.5" /> Panel</span>
      </div>
    </div>
  </div>
);

/* ---------- Act 5: INTERVIEW BRIEFS ---------- */
const BRIEF_ITEMS = [
  "Your story for this role, in 60 seconds",
  "STAR stories to lead with",
  "Likely questions & your strongest answers",
  "Day-of reminders and closing questions",
];
const SceneBriefs = () => (
  <div className="p-4 sm:p-5 h-full flex flex-col justify-center gap-2.5">
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-widest text-amber-400">Your Interview Brief</span>
      <span className="text-[8px] uppercase tracking-widest font-semibold text-zinc-400 border border-zinc-700 rounded px-1.5 py-0.5">Personalized</span>
    </div>
    <div className="hd-rise rounded-lg border border-zinc-800 bg-zinc-900/60 p-3.5 space-y-2">
      {BRIEF_ITEMS.map((t, i) => (
        <div key={i} className="hd-rise flex items-start gap-2" style={{ animationDelay: `${0.2 + i * 0.35}s` }}>
          <span className="mt-0.5 h-4 w-4 rounded bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
            <Check className="h-2.5 w-2.5 text-emerald-400" strokeWidth={3} />
          </span>
          <span className="text-[12px] text-zinc-200 leading-snug">{t}</span>
        </div>
      ))}
    </div>
    <div className="hd-rise inline-flex self-start items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-[10px] font-semibold text-emerald-300" style={{ animationDelay: "1.8s" }}>
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Built from your entire journey
    </div>
  </div>
);

export default function HeroDemo() {
  const [scene, setScene] = useState(0);
  const [phase, setPhase] = useState(0);
  const timers = useRef([]);

  useEffect(() => {
    if (!document.getElementById("hd-demo-css")) {
      const el = document.createElement("style");
      el.id = "hd-demo-css";
      el.textContent = CSS;
      document.head.appendChild(el);
    }
    PERSONAS.forEach((p) => { const im = new Image(); im.src = `/avatars/${p}_base.webp`; });
    ["half", "open", "ee", "o"].forEach((s) => { const im = new Image(); im.src = `/avatars/hiring_manager_${s}.webp`; });
  }, []);

  useEffect(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setPhase(0);
    const t = [];
    if (scene === 0) { t.push(setTimeout(() => setPhase(1), 3000), setTimeout(() => setPhase(2), 6200), setTimeout(() => setPhase(3), 9000)); }
    if (scene === 3) { t.push(setTimeout(() => setPhase(1), 8000), setTimeout(() => setPhase(2), 11000)); }
    t.push(setTimeout(() => setScene((s) => (s + 1) % SCENES.length), SCENES[scene].dur));
    timers.current = t;
    return () => t.forEach(clearTimeout);
  }, [scene]);

  return (
    <div data-testid="hero-demo" className="hd-anim relative select-none">
      <div className="absolute -inset-6 rounded-[2rem] bg-amber-500/[0.05] blur-3xl pointer-events-none" />
      <div className="relative rounded-2xl border border-zinc-800 bg-zinc-950/90 shadow-2xl overflow-hidden">
        {/* window chrome */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-zinc-800/80 bg-zinc-950">
          <span className="h-2.5 w-2.5 rounded-full bg-zinc-800" /><span className="h-2.5 w-2.5 rounded-full bg-zinc-800" /><span className="h-2.5 w-2.5 rounded-full bg-zinc-800" />
          <span className="ml-2 text-[10px] text-zinc-500">Intuiterview</span>
          <span key={scene} className="hd-rise ml-auto text-[9px] uppercase tracking-[0.2em] text-amber-400 font-semibold" data-testid="hero-demo-label">{SCENES[scene].label}</span>
        </div>
        {/* stage */}
        <div className="relative h-[300px] sm:h-[340px]" data-testid={`hero-demo-scene-${SCENES[scene].key}`}>
          {SCENES.map((s, i) => (
            <div key={s.key} className="absolute inset-0 transition-opacity duration-700 ease-out" style={{ opacity: scene === i ? 1 : 0, pointerEvents: "none", zIndex: scene === i ? 2 : 1 }}>
              {i === 0 && scene === 0 && <SceneApplication phase={phase} />}
              {i === 1 && scene === 1 && <ScenePredict />}
              {i === 2 && scene === 2 && <ScenePractice />}
              {i === 3 && <SceneOfficial active={scene === 3} phase={scene === 3 ? phase : 0} />}
              {i === 4 && scene === 4 && <SceneBriefs />}
            </div>
          ))}
        </div>
        {/* footer: tagline + progress */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-zinc-800/80 bg-zinc-950">
          <span key={`tag-${scene}`} className="hd-rise text-[11px] text-zinc-300">{SCENES[scene].tag}</span>
          <div className="flex items-center gap-1.5" data-testid="hero-demo-dots">
            {SCENES.map((_, i) => (
              <span key={i} className={`h-1 rounded-full transition-all duration-500 ${i === scene ? "w-5 bg-amber-400" : "w-1.5 bg-zinc-700"}`} />
            ))}
          </div>
        </div>
      </div>
      <div className="mt-2 text-right text-[10px] text-zinc-600">Illustrative demo, scores are examples, not real results</div>
    </div>
  );
}
